'use client';

import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { CalloutSlim as CalloutSlimType } from '@lib/types/cms/CalloutSlim';
import { toNonNullable } from '@lib/util/toNonNullable';
import { ButtonStyle, CalloutSlim as CalloutSlimUI } from 'ui-components';

type CalloutSlimProps = {
  data: CalloutSlimType;
  position: number;
};

const CalloutSlim = ({ data, position }: CalloutSlimProps) => {
  const {
    subheading,
    heading,
    description,
    image,
    cta,
    legalDisclaimer,
    imageFocusArea,
    title,
    trackAsPromo,
  } = data || {};
  const imageUrl = image?.url;
  const imageAlt = image?.alt;

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: title || '',
      typename: 'CalloutSlim',
      position,
      creativeTitle: toNonNullable(image?.title),
      trackAsPromo: trackAsPromo ?? false,
      ctas: cta?.label ? [cta.label] : [],
    });

  if (!heading || !imageUrl) {
    return null;
  }

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      <CalloutSlimUI
        title={heading}
        subtitle={subheading}
        image={{
          url: imageUrl,
          alt: imageAlt ?? '',
          height: toNonNullable(image?.height),
          width: toNonNullable(image?.width),
        }}
        imageFocusArea={imageFocusArea}
        description={description}
        action={{
          label: cta?.label!,
          url: cta?.url!,
          openInNewTab: cta?.openInNewTab ?? false,
          style:
            cta?.appearance === 'primary'
              ? ('primary' as ButtonStyle)
              : ('secondary' as ButtonStyle),
        }}
        legalDisclaimer={legalDisclaimer}
        onCtaClick={promoClickTrackingEvent}
      />
    </TrackPromotionImpression>
  );
};

export default CalloutSlim;
